<template>
  <v-stepper-content step="2">
    <validation-observer v-slot="{ invalid }">
      <v-card class="mx-1 mb-12">
        <v-card-title>
          <div class="mr-4">
            {{ $tc("form.labels.registration", 1).toUpperCase() }}
          </div>
          <v-btn
            small
            color="primary"
            @click="transferUserData"
            v-if="currentUser"
          >
            <v-icon small left>mdi-arrow-down</v-icon>
            {{
              $t("form.btn.transferData", { username: currentUser.username })
            }}
          </v-btn>
        </v-card-title>
        <v-card-text>
          <template v-for="(participant, index) in participants">
            <v-form :key="index">
              <v-row dense>
                <v-col cols="11">
                  <span class="headline py-4"
                    >{{ $t("form.labels.participant") }} {{ index + 1 }}</span
                  >
                </v-col>
                <v-col cols="1">
                  <v-btn
                    icon
                    :disabled="participants.length <= 1"
                    @click="removeParticipant(index)"
                  >
                    <v-icon color="primary">mdi-close</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
              <v-row class="hidden-lg-and-up mb-n6">
                <v-col>
                  <validation-provider
                    :name="$t('form.fields.salutation.label')"
                    v-slot="{ errors }"
                    rules="required"
                  >
                    <v-select
                      :label="$t('form.fields.salutation.label') + ' *'"
                      outlined
                      v-model="participant.salutation"
                      :items="genderTypes"
                      :error-messages="errors"
                    ></v-select>
                  </validation-provider>
                </v-col>
                <v-col>
                  <v-text-field
                    outlined
                    v-model="participant.title"
                    :label="$t('form.fields.title')"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="2" class="hidden-md-and-down">
                  <validation-provider
                    :name="$t('form.fields.salutation.label')"
                    v-slot="{ errors }"
                    rules="required"
                  >
                    <v-select
                      :label="$t('form.fields.salutation.label') + ' *'"
                      outlined
                      v-model="participant.salutation"
                      :items="genderTypes"
                      :error-messages="errors"
                    ></v-select>
                  </validation-provider>
                </v-col>
                <v-col>
                  <validation-provider
                    :name="$t('form.fields.firstname')"
                    v-slot="{ errors }"
                    rules="required"
                  >
                    <v-text-field
                      :label="$t('form.fields.firstname') + ' *'"
                      outlined
                      v-model="participant.firstname"
                      :error-messages="errors"
                    ></v-text-field>
                  </validation-provider>
                </v-col>
                <v-col>
                  <validation-provider
                    :name="$t('form.fields.lastname')"
                    v-slot="{ errors }"
                    rules="required"
                  >
                    <v-text-field
                      :label="$t('form.fields.lastname') + ' *'"
                      outlined
                      v-model="participant.lastname"
                      :error-messages="errors"
                    ></v-text-field>
                  </validation-provider>
                </v-col>
                <v-col cols="2" class="hidden-md-and-down">
                  <v-text-field
                    outlined
                    v-model="participant.title"
                    :label="$t('form.fields.title')"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row>
                <v-col>
                  <validation-provider
                    :name="$t('form.fields.email')"
                    v-slot="{ errors }"
                    rules="required|email"
                  >
                    <v-text-field
                      class="mt-n4"
                      :label="$t('form.fields.email') + ' *'"
                      outlined
                      v-model="participant.email"
                      type="email"
                      :error-messages="errors"
                    ></v-text-field>
                  </validation-provider>
                </v-col>
              </v-row>

              <v-row>
                <v-col>
                  <v-select
                    class="mt-n2"
                    :label="$t('form.fields.type')"
                    auto-select-first
                    outlined
                    :items="memberTypes"
                    @change="updatePriceTable"
                    v-model="participant.type"
                    :disabled="
                      participant.type === 'booth' && disableTypeSelection
                    "
                  />
                </v-col>
              </v-row>

              <template v-if="participant.type === 'member'">
                <v-row class="mt-n4" align="center">
                  <v-col md="5" cols="12">
                    <validation-provider
                      :name="$t('form.fields.membershipNumber')"
                      v-slot="{ errors }"
                      :rules="{
                        required: participant.documentLocation ? false : true,
                      }"
                    >
                      <v-text-field
                        :label="$t('form.fields.membershipNumber') + ' *'"
                        outlined
                        :error-messages="errors"
                        v-model="participant.membershipNumber"
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                  <v-col
                    md="1"
                    cols="12"
                    class="mt-n6 text-sm-left text-md-center py-0"
                    dense
                  >
                    <span class="subheading font-weight-medium">{{
                      $t("or")
                    }}</span>
                  </v-col>
                  <v-col md="6" cols="12">
                    <validation-provider
                      :name="$t('form.fields.membershipConfirmation')"
                      v-slot="{ errors }"
                      :rules="{
                        required: participant.membershipNumber ? false : true,
                      }"
                    >
                      <v-file-input
                        :label="$t('form.fields.membershipConfirmation') + ' *'"
                        outlined
                        :error-messages="errors"
                        v-model="participant.documentLocation"
                        @change="uploadDocument($event, participant)"
                      ></v-file-input>
                    </validation-provider>
                  </v-col>
                </v-row>
              </template>

              <template
                v-if="
                  participant.type === 'student' || participant.type === 'phd'
                "
              >
                <v-row class="mt-n4">
                  <v-col>
                    <validation-provider
                      :name="$t('form.fields.birthyear')"
                      v-slot="{ errors }"
                      rules="required"
                    >
                      <v-text-field
                        :label="$t('form.fields.birthyear') + ' *'"
                        outlined
                        :error-messages="errors"
                        v-model="participant.birthyear"
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                  <v-col>
                    <validation-provider
                      :name="$t('form.fields.certificateOfStudy')"
                      v-slot="{ errors }"
                      rules="required"
                    >
                      <v-file-input
                        :label="$t('form.fields.certificateOfStudy') + ' *'"
                        outlined
                        :error-messages="errors"
                        @change="uploadDocument($event, participant)"
                        v-model="participant.documentCertificateLocation"
                      ></v-file-input>
                    </validation-provider>
                  </v-col>
                </v-row>
              </template>

              <v-row class="mt-n4" v-if="participant.type === 'booth'">
                <v-col>
                  <validation-provider
                    :name="$t('form.fields.boothNumber')"
                    v-slot="{ errors }"
                    rules="required"
                  >
                    <v-text-field
                      :label="$t('form.fields.boothNumber') + ' *'"
                      outlined
                      :error-messages="errors"
                      v-model="participant.boothNumber"
                      :disabled="disableTypeSelection"
                    ></v-text-field>
                  </validation-provider>
                </v-col>
              </v-row>

              <v-row class="mt-n4" dense>
                <v-col cols="12">{{ $t("form.fields.showInFolder") }}</v-col>
                <v-col class="mt-n4">
                  <validation-provider
                    :name="$t('form.fields.showInFolder')"
                    v-slot="{ errors }"
                    rules="required"
                  >
                    <v-radio-group
                      :value="participant.showInFolder"
                      row
                      mandatory
                      :error-messages="errors"
                      dense
                      @change="
                        setParticipantShowInFolder({
                          participant,
                          index,
                          value: $event,
                        })
                      "
                    >
                      <v-radio :label="$t('yes')" :value="true"></v-radio>
                      <v-radio :label="$t('no')" :value="false"></v-radio>
                    </v-radio-group>
                  </validation-provider>
                </v-col>
              </v-row>

              <v-row class="mt-n8" dense v-if="index > 0">
                <v-col cols="12">
                  <v-switch
                    v-model="participant.differentContactAddress"
                    :label="$t('form.fields.differentContactAddress')"
                  />
                </v-col>
                <v-col cols="12" v-if="participant.differentContactAddress">
                  <v-expansion-panels
                    :value="participant.differentContactAddress ? 0 : null"
                    class="mt-n6 mb-6"
                    elevation="0"
                  >
                    <v-expansion-panel>
                      <v-expansion-panel-header>
                        {{ $t("form.fields.contactAddress") }}
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-row class="mt-n6">
                          <v-col cols="12" class="mb-n8">
                            <validation-provider
                              :name="$t('form.fields.company')"
                              v-slot="{ errors }"
                              rules="required"
                            >
                              <v-text-field
                                :label="$t('form.fields.company') + ' *'"
                                outlined
                                v-model="participant.contactCompany"
                                required
                                :error-messages="errors"
                              ></v-text-field>
                            </validation-provider>
                          </v-col>
                          <v-col>
                            <validation-provider
                              :name="$t('form.fields.street')"
                              v-slot="{ errors }"
                              rules="required"
                            >
                              <v-text-field
                                :label="$t('form.fields.street') + ' *'"
                                outlined
                                v-model="participant.contactStreet"
                                required
                                :error-messages="errors"
                              ></v-text-field>
                            </validation-provider>
                          </v-col>
                          <v-col cols="2">
                            <validation-provider
                              :name="$t('form.fields.zip')"
                              v-slot="{ errors }"
                              rules="required"
                            >
                              <v-text-field
                                :label="$t('form.fields.zip') + ' *'"
                                outlined
                                v-model="participant.contactZip"
                                required
                                :error-messages="errors"
                              ></v-text-field>
                            </validation-provider>
                          </v-col>
                          <v-col cols="3">
                            <validation-provider
                              :name="$t('form.fields.city')"
                              v-slot="{ errors }"
                              rules="required"
                            >
                              <v-text-field
                                :label="$t('form.fields.city') + ' *'"
                                outlined
                                v-model="participant.contactCity"
                                required
                                :error-messages="errors"
                              ></v-text-field>
                            </validation-provider>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col class="mt-n8 mb-n6">
                            <validation-provider
                              :name="$t('form.fields.country')"
                              v-slot="{ errors }"
                              rules="required"
                            >
                              <v-autocomplete
                                :label="$t('form.fields.country') + ' *'"
                                outlined
                                :items="countries"
                                item-text="value"
                                item-value="key"
                                :error-messages="errors"
                                v-model="participant.contactCountry"
                              />
                            </validation-provider>
                          </v-col>
                        </v-row>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-col>
              </v-row>

              <v-row class="mt-n4" dense v-if="showToggleVirtualOnly">
                <v-col cols="12">{{ $t("form.labels.participation") }}</v-col>
                <v-col class="mt-n4">
                  <validation-provider
                    :name="$t('form.labels.participation')"
                    v-slot="{ errors }"
                    rules="required"
                  >
                    <v-radio-group
                      :value="participant.virtualOnly"
                      row
                      mandatory
                      :error-messages="errors"
                      dense
                      @change="
                        setParticipantVirtualOnly({
                          participant,
                          index,
                          value: $event,
                        })
                      "
                    >
                      <v-radio
                        :label="$t('form.labels.onSite')"
                        :value="false"
                      ></v-radio>
                      <v-radio
                        :label="$t('form.labels.virtual')"
                        :value="true"
                      ></v-radio>
                    </v-radio-group>
                  </validation-provider>
                </v-col>
              </v-row>

              <span class="headline py-4">
                {{ $tc("form.labels.registration", 1) }}
              </span>
              <div
                v-for="category in getCategorizedPricingTable(
                  participant,
                  index
                )"
                :key="category.id"
              >
                <template v-if="category.id">
                  <v-divider class="my-4" />
                  <v-btn
                    @click="toggleCategory(`${index}-${category.id}`)"
                    outlined
                    color="primary"
                  >
                    <v-icon
                      v-if="
                        visibleCategories.indexOf(`${index}-${category.id}`) >=
                          0
                      "
                      >mdi-minus</v-icon
                    >
                    <v-icon v-else>mdi-plus</v-icon>
                    {{ category.title }}
                  </v-btn>
                </template>
                <div
                  v-if="
                    category.id === '' ||
                      visibleCategories.indexOf(`${index}-${category.id}`) >= 0
                  "
                >
                  <div
                    :key="price._id"
                    class="pl-sm-1 mb-sm-n8"
                    v-for="price in category.prices"
                  >
                    <v-row dense align="center">
                      <v-col v-if="price.mandatory">
                        <v-checkbox
                          :label="priceTitle(price, $i18n.locale)"
                          readonly
                          :input-value="true"
                        />
                      </v-col>
                      <v-col v-else>
                        <v-checkbox
                          :label="priceTitle(price, $i18n.locale)"
                          :input-value="
                            participantSelection(index, price.slug).key
                          "
                          @change="
                            toggleSelectedPackage({ index, key: price.slug })
                          "
                        />
                      </v-col>
                      <v-col>
                        <a href="#" @click="showInfo(price, $event)"
                          >({{ $t("form.labels.moreInfo") }})</a
                        >
                      </v-col>
                      <v-col>
                        {{
                          todaysPrice(
                            getParticipantPrices(participant, price).prices
                          ).price
                        }}
                        €
                        {{
                          price.priceType.includes("multiple")
                            ? $t("form.labels.perPerson")
                            : ""
                        }}
                        ({{
                          $t("form.labels.vatLabel", [
                            todaysPrice(
                              getParticipantPrices(participant, price).prices
                            ).taxRate,
                          ])
                        }})
                      </v-col>
                    </v-row>

                    <template
                      v-if="
                        (price.priceType === 'speaker' ||
                          price.priceType === 'webinar') &&
                          participantSelection(index, price.slug).value != ''
                      "
                    >
                      <v-checkbox
                        class="ml-sm-9 mt-sm-n5"
                        :label="$t('form.labels.speaker')"
                        :input-value="
                          participantSelection(index, price.slug + '-speaker')
                            .value != ''
                        "
                        @change="
                          toggleSelectedPackage({
                            index,
                            key: price.slug + '-speaker',
                            value: $event,
                          })
                        "
                      />
                    </template>
                    <template
                      v-if="
                        price.priceType === 'multiplePersons' &&
                          participantSelection(index, price.slug).value != ''
                      "
                    >
                      <v-text-field
                        class="ml-sm-9 mt-sm-n5"
                        type="number"
                        min="1"
                        :label="$t('form.labels.multiplePersons')"
                        :value="
                          participantSelection(
                            index,
                            price.slug + '-' + price.priceType
                          ).value || 1
                        "
                        @input="
                          updateSelectedPackage({
                            index,
                            key: price.slug + '-' + price.priceType,
                            value: $event && $event > 0 ? $event : 1,
                            type: price.priceType,
                          })
                        "
                      />
                    </template>
                    <template
                      v-if="
                        price.priceType === 'multiplePersonsWithName' &&
                          participantSelection(index, price.slug).value != ''
                      "
                    >
                      <v-combobox
                        class="ml-sm-9 mt-sm-n5"
                        :label="$t('form.labels.multiplePersonsWithName')"
                        dense
                        outlined
                        no-data
                        chips
                        multiple
                        :value="
                          participantSelection(
                            index,
                            price.slug + '-' + price.priceType
                          )
                            .value.split(',')
                            .filter((a) => a !== '')
                        "
                        @change="
                          updateSelectedPackage({
                            index,
                            key: price.slug + '-' + price.priceType,
                            value: $event.join(','),
                            type: price.priceType,
                          })
                        "
                      >
                        <template v-slot:selection="{ item }">
                          <v-chip
                            close
                            @click:close="
                              removeSelectedItem(index, price, item)
                            "
                          >
                            {{ item }}
                          </v-chip>
                        </template>
                      </v-combobox>
                    </template>
                  </div>
                </div>
              </div>
              <v-row class="mt-4 mb-4" v-if="multiple" dense align="center">
                <v-col>
                  <span class="title">{{ $t("form.labels.subtotal") }}:</span>
                </v-col>
                <v-col />
                <v-col>
                  <span class="title">
                    {{ participantCost(participant) }} €
                  </span>
                </v-col>
              </v-row>

              <v-divider v-if="multiple && participants.length > 1" />
            </v-form>
          </template>
          <v-row justify="center" :class="multiple ? 'mt-2' : ''">
            <v-btn color="primary" raised @click="handleAddParticipant">
              <v-icon>mdi-plus</v-icon>
              &nbsp;{{ $t("form.labels.addParticipant") }}
            </v-btn>
          </v-row>

          <v-divider class="mt-3" v-if="participants.length === 1" />
          <v-row dense align="center">
            <v-col>
              <span class="headline font-weight-bold"
                >{{ $t("form.labels.total") }}:</span
              >
            </v-col>
            <v-col />
            <v-col class="headline font-weight-bold">{{ cartSum }} €</v-col>
          </v-row>
        </v-card-text>
      </v-card>
      <v-dialog
        v-model="dialog.active"
        max-width="600px"
        :attach="dialog.attach"
      >
        <v-card>
          <v-card-title>{{ dialog.title }}</v-card-title>
          <v-card-text>{{ dialog.info }}</v-card-text>
        </v-card>
      </v-dialog>
      <v-layout>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          class="mr-4"
          @click="$emit('onContinue')"
          :disabled="invalid"
          >{{ $t("form.btn.continue") }}</v-btn
        >
        <v-btn text @click="$emit('onBack')">{{ $t("form.btn.back") }}</v-btn>
      </v-layout>
    </validation-observer>
  </v-stepper-content>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import { attachmentResource } from "../../api/resources";
import { GenderTypes, MemberTypes } from "../../config/constants";
import de from "../../i18n/de.json";

export default {
  props: ["multiple", "pageType"],

  async created() {
    this.clearParticipantFiles();
    const urlParams = new URLSearchParams(window.location.search);
    this.disableTypeSelection = urlParams.has("booth");
    await this.getPrice();
    this.toggleMandatoryAndPreselectedPackages();
  },

  data() {
    return {
      dialog: {
        active: false,
        attach: false,
        title: "",
        info: "",
      },
      visibleCategories: [],
      disableTypeSelection: false,
    };
  },
  methods: {
    ...mapActions([
      "addParticipant",
      "removeParticipant",
      "getPrice",
      "toggleSelectedPackage",
      "updateSelectedPackage",
      "clearParticipantFiles"
    ]),
    ...mapMutations([
      "setParticipantShowInFolder",
      "setParticipantVirtualOnly",
    ]),

    async handleAddParticipant() {
      await this.addParticipant();
      this.toggleMandatoryAndPreselectedPackages();
    },

    transferUserData() {
      this.participants[0].salutation = this.currentUser.attributes.salutation;
      this.participants[0].firstname = this.currentUser.firstName;
      this.participants[0].lastname = this.currentUser.lastName;
      this.participants[0].title = this.currentUser.attributes.title;
      this.participants[0].email = this.currentUser.email;
      this.participants[0].type = "member";
      this.participants[0].membershipNumber = this.currentUser.username;
    },

    removeSelectedItem(index, price, item) {
      const selected = this.participantSelection(
        index,
        price.slug + "-" + price.priceType
      ).value.split(",");
      selected.splice(selected.indexOf(item), 1);
      this.updateSelectedPackage({
        index,
        key: price.slug + "-" + price.priceType,
        value: selected.join(","),
        type: price.priceType,
      });
    },

    showInfo(price, $event) {
      $event.preventDefault();
      this.dialog.title = this.priceTitle(price, this.$i18n.locale);
      this.dialog.info = this.priceInfoText(price, this.$i18n.locale);
      this.dialog.active = true;
    },

    hideDependantPrice(index, price) {
      if (!price._dependantBy || !price._dependantBy.slug) {
        return false;
      }
      return !this.participantSelection(index, price._dependantBy.slug).key;
    },

    async updatePriceTable(value) {
      await this.getPrice(value);
    },

    async uploadDocument($data, participant) {
      if ($data) {
        let formData = new FormData();
        formData.append("file", $data);
        const data = await attachmentResource.post("", formData, {
          headers: { "x-xsrf-token": this.$cookies.get("oegg-cms.csrf") },
        });
        participant.document = data.data;
      }
    },

    toggleMandatoryAndPreselectedPackages() {
      for (let pricingTable of this.pricingTable) {
        if (
          !pricingTable.hide &&
          !this.disableTypeSelection &&
          (pricingTable.mandatory || pricingTable.defaultSelected)
        ) {
          for (let i = 0; i < this.participants.length; ++i) {
            this.updateSelectedPackage({ key: pricingTable.slug, index: i });
          }
        }
      }
    },

    getParticipantPrices(participant, price) {
      return price[participant.type] || price["none"];
    },

    getCategorizedPricingTable(participant, index) {
      const visiblePrices = this.pricingTable.filter(
        (price) =>
          !price.hide &&
          this.todaysPrice(
            this.getParticipantPrices(participant, price).prices
          ) &&
          (!this.showToggleVirtualOnly ||
            (participant.virtualOnly && price.virtual) ||
            (!participant.virtualOnly && !price.virtual)) &&
          !this.hideDependantPrice(index, price)
      );
      const categoryIds = [
        ...new Set(
          visiblePrices
            .filter((entry) => entry.categoryId)
            .sort((a, b) => a._category.orderIndex - b._category.orderIndex)
            .map((entry) => entry.categoryId)
        ),
      ];
      const categorizedPrizes = [
        {
          id: "",
          prices: visiblePrices.filter((entry) => !entry.categoryId),
        },
      ];
      categoryIds.forEach((categoryId) => {
        const categorizedPrices = visiblePrices.filter(
          (entry) => entry.categoryId === categoryId
        );
        categorizedPrices.sort((a, b) => {
          const title1 =
            this.$i18n.locale === "en"
              ? a.title_en
              : this.$i18n.locale === "es"
              ? a.title_es
              : a.title;
          const title2 =
            this.$i18n.locale === "en"
              ? b.title_en
              : this.$i18n.locale === "es"
              ? b.title_es
              : b.title;
          return title1.localeCompare(title2);
        });
        const categoryObject = categorizedPrices[0]._category;
        const categoryTitle =
          this.$i18n.locale === "en"
            ? categoryObject.title_en
            : this.$i18n.locale === "es"
            ? categoryObject.title_es
            : categoryObject.title;
        categorizedPrizes.push({
          id: categoryId,
          title: categoryTitle,
          prices: categorizedPrices,
        });
      });
      return categorizedPrizes;
    },
    toggleCategory(key) {
      const findIndex = this.visibleCategories.indexOf(key);
      if (findIndex < 0) {
        this.visibleCategories.push(key);
      } else {
        this.visibleCategories.splice(findIndex, 1);
      }
    },
  },
  computed: {
    ...mapGetters([
      "currentUser",
      "participants",
      "pricingTable",
      "participantCost",
      "cartSum",
      "todaysPrice",
      "participantSelection",
      "priceTitle",
      "priceInfoText",
    ]),
    memberTypes() {
      return Object.keys(MemberTypes)
        .filter((type) => this.pageType === "ISRM" || type != "PHD")
        .map((type) => {
          return {
            value: MemberTypes[type],
            text: this.$t(`form.memberTypes.${type}`),
          };
        });
    },
    genderTypes() {
      return GenderTypes.map((t) => {
        return { value: t, text: this.$t(`form.fields.salutation.${t}`) };
      });
    },
    countries() {
      return Object.keys(de.countries).map((countryKey) => {
        return {
          key: countryKey,
          value: this.$t(`countries.${countryKey}`),
        };
      });
    },
    showToggleVirtualOnly() {
      return (
        this.pricingTable.length !==
        this.pricingTable.filter((entry) => !entry.virtual).length
      );
    },
  },
};
</script>

<style>
@media (min-width: 465px) {
  .price-entry {
    padding-left: 4px;
    margin-bottom: -30px;
  }
}
</style>
